import {
  type CreatePackageFaqRequest,
  type PackageDetail,
  type PackageFaqOut,
} from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { ChevronDown, HelpCircle, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useAddPackageFaq, useDeletePackageFaq, useUpdatePackageFaq } from '../use-packages';

import { PackageFaqFormDrawer } from './PackageFaqFormDrawer';

interface FaqsEditorProps {
  /** Live (edit) mode: the existing package whose FAQs are edited via the API. */
  pkg?: PackageDetail | null;
  /** Controlled (create) mode: staged FAQs held in the parent. */
  draftValue?: CreatePackageFaqRequest[];
  /** Controlled (create) mode: provide to switch to in-memory editing (no API). */
  onDraftChange?: (items: CreatePackageFaqRequest[]) => void;
}

/** Build a {@link PackageFaqOut}-shaped row from a draft FAQ for the list + edit drawer. */
const draftToRow = (f: CreatePackageFaqRequest, i: number): PackageFaqOut => ({
  id: `draft-${i}`,
  question: f.question,
  answerHtml: f.answerHtml,
  sortOrder: f.sortOrder ?? i,
});

export const FaqsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: FaqsEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftItems = draftValue ?? [];

  // Hooks run unconditionally; in controlled mode the mutations never fire.
  const add = useAddPackageFaq(pkg?.id ?? '');
  const update = useUpdatePackageFaq(pkg?.id ?? '');
  const del = useDeletePackageFaq(pkg?.id ?? '');
  const [drawer, setDrawer] = useState<{
    mode: 'create' | 'edit';
    item?: PackageFaqOut;
    draftIndex?: number;
  } | null>(null);

  const rows: PackageFaqOut[] = controlled ? draftItems.map(draftToRow) : (pkg?.faqs ?? []);
  const pending = controlled ? false : add.isPending || update.isPending || del.isPending;

  const submit = async (payload: CreatePackageFaqRequest): Promise<void> => {
    if (controlled) {
      const next =
        drawer?.mode === 'edit' && drawer.draftIndex !== undefined
          ? draftItems.map((d, i) => (i === drawer.draftIndex ? payload : d))
          : [...draftItems, payload];
      onDraftChange(next.map((f, i) => ({ ...f, sortOrder: i })));
      setDrawer(null);
      return;
    }
    if (drawer?.mode === 'edit' && drawer.item) {
      await update.mutateAsync({ childId: drawer.item.id, payload });
    } else {
      await add.mutateAsync(payload);
    }
    setDrawer(null);
  };

  const remove = (row: PackageFaqOut, i: number): void => {
    if (!window.confirm('Remove this FAQ?')) return;
    if (controlled) {
      onDraftChange(
        draftItems.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, sortOrder: idx })),
      );
      return;
    }
    del.mutate(row.id);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">FAQs</h3>
        <UzButton size="sm" onClick={() => setDrawer({ mode: 'create' })} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add FAQ
        </UzButton>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No FAQs yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row, i) => {
            const isOpen = drawer?.mode === 'edit' && drawer.draftIndex === i;
            const toggle = (): void =>
              setDrawer(isOpen ? null : { mode: 'edit', item: row, draftIndex: i });
            return (
              <React.Fragment key={row.id}>
                <li
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-zinc-200 px-3 py-2 hover:bg-zinc-50"
                >
                  <HelpCircle className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                    {row.question}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(row, i);
                    }}
                    disabled={pending}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove FAQ"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                {isOpen && (
                  <li className="list-none">
                    <PackageFaqFormDrawer
                      mode="edit"
                      item={row}
                      pending={pending}
                      onClose={() => setDrawer(null)}
                      onSubmit={submit}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {drawer?.mode === 'create' && (
        <PackageFaqFormDrawer
          mode="create"
          pending={pending}
          onClose={() => setDrawer(null)}
          onSubmit={submit}
        />
      )}
    </section>
  );
};
